import { useEffect, useRef, useState } from 'react'
import { IconButton, Label } from '@nike/eds'
import { Calendar, CaretLeft, CaretRight, Close } from '@nike/nike-design-system-icons'

const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

type MonthYearPickerProps = {
    id: string
    label: string
    value: string // "YYYY-MM" format
    onChange: (value: string | undefined) => void
}

function parseValue(value: string): { year: number; month: number } | null {
    if (!value) return null
    const [y, m] = value.split('-').map(Number)
    if (!y || !m || m < 1 || m > 12) return null
    return { year: y, month: m }
}

function formatDisplay(value: string): string {
    const parsed = parseValue(value)
    if (!parsed) return ''
    return `${MONTHS[parsed.month - 1]} ${parsed.year}`
}

export function MonthYearPicker({ id, label, value, onChange }: MonthYearPickerProps) {
    const parsed = parseValue(value)
    const now = new Date()
    const [isOpen, setIsOpen] = useState(false)
    const [viewYear, setViewYear] = useState(parsed?.year ?? now.getFullYear())
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (isOpen && parsed?.year) {
            setViewYear(parsed.year)
        }
    }, [isOpen, parsed?.year])

    useEffect(() => {
        if (!isOpen) return
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen])

    const handleSelect = (monthIndex: number) => {
        const mm = String(monthIndex + 1).padStart(2, '0')
        onChange(`${viewYear}-${mm}`)
        setIsOpen(false)
    }

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation()
        onChange(undefined)
        setIsOpen(false)
    }

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <Label htmlFor={id} font="title-6">
                {label}
            </Label>
            <div
                id={id}
                role="button"
                tabIndex={0}
                onClick={() => setIsOpen(prev => !prev)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsOpen(prev => !prev) }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    border: '1px solid var(--eds-color-grey-2, #CACACB)',
                    borderRadius: 'var(--eds-radii-4, 4px)',
                    padding: '15px 16px',
                    cursor: 'pointer',
                    font: 'var(--eds-type-body-2)',
                    background: 'var(--eds-color-white, #fff)',
                    marginTop: '15px',
                }}
            >
                <span style={{ color: value ? '#333' : 'var(--eds-color-text-secondary, #707070)' }}>
                    {value ? formatDisplay(value) : 'Select month…'}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '-8px' }}>
                    {value && (
                        <IconButton
                            icon={<Close />}
                            label="Clear"
                            size="small"
                            variant="ghost"
                            onClick={handleClear}
                            style={{ marginTop: '-8px', marginBottom: '-8px' }}
                        />
                    )}
                    <Calendar style={{ width: '20px', height: '20px', color: 'var(--eds-color-grey-2, #CACACB)', flexShrink: 0 }} />
                </div>
            </div>

            {isOpen && (
                <div style={{
                    position: 'absolute',
                    zIndex: 1000,
                    top: '100%',
                    left: 0,
                    marginTop: '4px',
                    background: 'var(--eds-color-white, #fff)',
                    border: '1px solid var(--eds-color-grey-2, #CACACB)',
                    borderRadius: 'var(--eds-radii-8, 8px)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    padding: '12px',
                    width: '260px',
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                    }}>
                        <IconButton
                            icon={<CaretLeft />}
                            label="Previous year"
                            size="small"
                            variant="ghost"
                            onClick={() => setViewYear(y => y - 1)}
                            style={{ color: 'var(--eds-color-black, #111)' }}
                        />
                        <span style={{ font: 'var(--eds-type-title-6)', userSelect: 'none' }}>{viewYear}</span>
                        <IconButton
                            icon={<CaretRight />}
                            label="Next year"
                            size="small"
                            variant="ghost"
                            onClick={() => setViewYear(y => y + 1)}
                            style={{ color: 'var(--eds-color-black, #111)' }}
                        />
                    </div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '4px',
                    }}>
                        {MONTHS.map((name, idx) => {
                            const isSelected = parsed?.year === viewYear && parsed?.month === idx + 1
                            return (
                                <button
                                    key={name}
                                    type="button"
                                    onClick={() => handleSelect(idx)}
                                    style={{
                                        padding: '8px 4px',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontWeight: isSelected ? 700 : 400,
                                        background: isSelected ? 'var(--eds-color-background-active, #111)' : 'transparent',
                                        color: isSelected ? 'var(--eds-color-black, #111)' : '#333',
                                        fontSize: '13px',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isSelected) e.currentTarget.style.background = 'var(--eds-color-background-secondary, #f5f5f5)'
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isSelected) e.currentTarget.style.background = 'transparent'
                                    }}
                                >
                                    {name}
                                </button>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}
